import { GetBuRuleCollectionModel } from '../../../../../../models/rules/rules.models';
import { NotificationClientService, NotificationType } from '../../../../../../services/basics/notification-client.service';
import { CustomerSubscriptionModel, SubscriptionTypeEnum } from '../../../../../../models/customers/customer-subscription.models';
import { autoinject } from "aurelia-dependency-injection";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { CustomerSubscriptionService } from "../../../../../../services/modules/customer-subscription-client.service";
import { computedFrom } from 'aurelia-binding';
import { RightShipDialogServiceConstants } from '../../../../../../constants/basics/rightship-dialog.constants';
import { UserClientService } from '../../../../../../services/authentication/user-client.service';
import { ValidationsClientService } from '../../../../../../services/validations/validations-client.service';
import { CustomerService } from '../../../../../../services/modules/customer-client.service';
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { CustomValidationRenderer } from '../../../../../../client-validation-system/custom-validation-renderer';
import * as moment from "moment";
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { PaymentTypeEnum, RightStoreCheckoutPurchaseModel } from '../../../../../../models/rightstore/rightstore.models';
import { RightStoreService } from '../../../../../../services/rightstore/rightstore.service';
import { UserClaimsService } from '../../../../../../services/authentication/user-claims.service';
import { Claims } from '../../../../../../constants/authentication/claims';
import { QueriesClientService } from '../../../../../../services/queries/queries-client.services';
import { RegexConstants } from '@rightship-tech/common-ui/constants/regex.constants';

@autoinject
export class EditCustomerAPISubscriptionDialog {

    public customerSubscriptionModel: CustomerSubscriptionModel;
    public customerId: number;
    public productCode: string;
    public ruleCollections: GetBuRuleCollectionModel[];
    public bussinessUnits = [];
    public isLoading: boolean;

    private currentSubscriptionModel: CustomerSubscriptionModel;
    validator: ValidationController;

    afterSave: () => void;
    isRightshipUser: boolean;
    constructor(private dialogClientService: DialogClientService, private customerSubscriptionService: CustomerSubscriptionService,
        private userClientService: UserClientService,
        public notificationClientService: NotificationClientService,
        public validationsClientService: ValidationsClientService,
        public customerService: CustomerService,
        validationControllerFactory: ValidationControllerFactory,
        private configService: ConfigService,
        private queryService: QueriesClientService,
        private rightStoreService: RightStoreService,
        private userClaimsService: UserClaimsService) {
        this.validator = validationControllerFactory.createForCurrentScope();
        this.validator.addRenderer(new CustomValidationRenderer());
        this.isRightshipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
    }

    @computedFrom('customerSubscriptionModel', 'customerSubscriptionModel.trialLength', 'customerSubscriptionModel.startDate')
    get trialEndDate() {
        if (this.customerSubscriptionModel && (this.customerSubscriptionModel.trialLength != null || this.customerSubscriptionModel.trialLength == 0) && this.customerSubscriptionModel.startDate) {
            const endDate = new Date(this.customerSubscriptionModel.startDate);
            endDate.setDate(endDate.getDate() + this.customerSubscriptionModel.trialLength);
            return endDate;
        }
        return null;
    }
    @computedFrom('customerSubscriptionModel', 'trialEndDate', 'customerSubscriptionModel.subscriptionLength', 'customerSubscriptionModel.startDate')
    get subscriptionEndDate() {
        if (this.customerSubscriptionModel && this.customerSubscriptionModel.subscriptionLength && this.customerSubscriptionModel.startDate) {
            const endDate = this.trialEndDate ? new Date(this.trialEndDate) : new Date(this.customerSubscriptionModel.startDate);
            endDate.setMonth(endDate.getMonth() + this.customerSubscriptionModel.subscriptionLength);
            return endDate;
        }
        return null;
    }

    activate(model: { customerId: number, productCode: string, productName: string, productId: number, afterSave: () => void }) {
        this.customerId = model.customerId;
        this.productCode = model.productCode;

        //get rule collection
        this.customerSubscriptionService.getRuleCollection(this.customerId).then(response => {
            this.ruleCollections = response;
        });

        this.queryService.getData(`odata/BusinessUnits?$filter=(customerId eq ${this.customerId})`).then(response => {
            this.bussinessUnits =  response.value; 
        });

        this.customerSubscriptionService.getCustomerSubscription(model.customerId, model.productCode).then(response => {
            this.customerSubscriptionModel = response.customerSubscription || {
                productId: model.productId,
                customerId: model.customerId,
                productName: model.productName,
                currentCompaniesCount: 0,
                currentCompanyChangesCount: 0,
                currentVesselChangesCount: 0,
                currentVesselsCount: 0,
                isExpired: false,
                includeCompanyData: false,
                maxCompaniesCount: 0,
                maxCompanyChangesCount: 0,
                maxVesselChangesCount: 0,
                maxVesselsCount: 0,
                startDate: null,
                trialLength: 0,
                subscriptionLength: 0,
                //set default subscription type to full for API subscriptions
                subscriptionTypeId: null,
                emailNotifications: [{
                    email: "",
                    emailRecipientTypeId: 1
                }],
                ruleCollectionId: null
            };

            //add email to show in UI in case of not having any email
            if(response.customerSubscription) {
                if(response.customerSubscription.emailNotifications.length == 0) {
                    this.customerSubscriptionModel.emailNotifications = [];
                    this.customerSubscriptionModel.emailNotifications.push({
                        email: "",
                        emailRecipientTypeId: 1,
                    });
                }
            }

            this.currentSubscriptionModel = { ...this.customerSubscriptionModel };

            this.validator.addObject(this.customerSubscriptionModel, this.getRules());
            
            this.afterSave = model.afterSave;
        });
    }

    @computedFrom("customerSubscriptionModel.subscriptionLength",
        "customerSubscriptionModel.subscriptionTypeId",
        "customerSubscriptionModel.trialLength")
    get shouldDisableActivation() {
        if (this.customerSubscriptionModel) {
            return this.shouldTriggerTransaction ? this.customerSubscriptionModel.subscriptionLength < 1: false;
        }
        return true;
    }

    convertEmailQuaryString() {
        //convert emails to query string
        const result = this.customerSubscriptionModel.emailNotifications.map((item, index) => 
        `EmailNotifications[${index}][Email]=${encodeURIComponent(item.email)}&EmailNotifications[${index}][EmailRecipientTypeId]=${item.emailRecipientTypeId}`).join('&');

        return result;
    }

    async activateSubscription(): Promise<void> {
        const isValid = await this.validator.validate();
        if (isValid.valid) {
            if (this.shouldTriggerTransaction) {
                this.openRightStoreCheckoutDialog();
            } else {
                await this.customerSubscriptionService.createOrUpdateCustomerSubscription({
                    customerId: this.customerSubscriptionModel.customerId,
                    productId: this.customerSubscriptionModel.productId,
                    productCode: this.productCode,
                    companiesCount: this.customerSubscriptionModel.maxCompaniesCount,
                    companyChangesCount: this.customerSubscriptionModel.maxCompanyChangesCount,
                    includeCompanyData: this.customerSubscriptionModel.includeCompanyData,
                    //set default subscription type to full for API subscriptions
                    subscriptionTypeId: null,
                    vesselsCount: 0,
                    vesselChangesCount: 0,
                    startDate: this.customerSubscriptionModel.startDate,
                    trialLength: this.customerSubscriptionModel.trialLength,
                    subscriptionLength: this.customerSubscriptionModel.subscriptionLength,
                    totalPrice: this.customerSubscriptionModel.totalPrice,
                    emailNotifications: this.customerSubscriptionModel.emailNotifications,
                    ruleCollectionId: this.customerSubscriptionModel.ruleCollectionId,
                    businessUnitId : this.customerSubscriptionModel.businessUnitId
                });
                this.dialogClientService.hide(true, "default", true);
                this.notificationClientService.show("Subscription was purchased successfully and will be updated soon!", "", NotificationType.Success);
                
                if(this.afterSave) {
                    this.afterSave();
                }
                if(this.shouldCleanVesselList) {
                    this.cleanVesselList();
                }
            }
        }
    }

    //Open rightstore checkout dialog
    async openRightStoreCheckoutDialog() {
            let queryParams = this.configService.config.environment.toLowerCase() == "production" ? "" : `env=${this.configService.config.environment}`;
            if (queryParams) queryParams += "&";
            queryParams += `IncludeCompanyData=${this.customerSubscriptionModel.includeCompanyData}`;
            queryParams += `&CompaniesCount=${this.customerSubscriptionModel.maxCompaniesCount}`;
            queryParams += `&CompanyChangesCount=${this.customerSubscriptionModel.maxCompanyChangesCount}`;
            queryParams += `&StartDate=${moment(this.customerSubscriptionModel.startDate).toISOString()}`;
            queryParams += `&TrialLength=${this.customerSubscriptionModel.trialLength}`;
            queryParams += `&SubscriptionLength=${this.customerSubscriptionModel.subscriptionLength}`;
            //set default subscription type to full for API subscriptions
            queryParams += `&SubscriptionTypeId=${SubscriptionTypeEnum.Full}`;
            queryParams += `&UserId=${this.userClientService?.currentUser?.id}`;
            queryParams += `&RuleCollectionId=${this.customerSubscriptionModel.ruleCollectionId}`;
            queryParams += `&BusinessUnitId=${this.customerSubscriptionModel.businessUnitId}`;
            queryParams += `&${this.convertEmailQuaryString()}`;
                    
            const customerBUIds = await this.customerService.getCustomerBusinessUnitIds(this.customerId);
            if (customerBUIds.businessUnitIds.length == 0) {
                this.notificationClientService.show("There is no business unit assigned to this customer", null, NotificationType.Error);
            } else {
                const customerModel = await this.customerService.getCustomer(this.customerId);
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightstoreCheckoutDialog,
                    {
                        userName: this.userClientService.currentUser.email,
                        customerNumber: customerBUIds.businessUnitIds[0],
                        productNumber: this.productCode,
                        quantity: 0,
                        preventRedirect: true,
                        queryParams: queryParams,
                        canCustomerInvoice: customerModel.customer.canInvoice,
                        showCreateOpportunityDialog: true,
                        afterPurchase: (() => {
                            this.notificationClientService.show("Subscription was purchased successfully and will be updated soon!", "", NotificationType.Success);
                            this.dialogClientService.hide(true, "default", true);
    
                            if(this.afterSave) {
                                this.afterSave();
                            }
                            if(this.shouldCleanVesselList) {
                                this.cleanVesselList();
                            }
                        }).bind(this)
                    }, true, false, false, 'right');
            }
    }

    //Cancel
    cancel() {
        this.dialogClientService.hide();
    }

    @computedFrom("customerSubscriptionModel.maxCompaniesCount",
        "customerSubscriptionModel.subscriptionLength",
        "customerSubscriptionModel.startDate",
        "customerSubscriptionModel.subscriptionTypeId")
    get shouldTriggerTransaction() {
        return this.customerSubscriptionModel && this.currentSubscriptionModel && (this.customerSubscriptionModel.maxCompaniesCount != this.currentSubscriptionModel.maxCompaniesCount ||
            (this.customerSubscriptionModel.subscriptionLength != this.currentSubscriptionModel.subscriptionLength && this.customerSubscriptionModel.subscriptionLength > 0) ||
            !moment(this.customerSubscriptionModel.startDate).isSame(moment(this.currentSubscriptionModel.startDate), 'day'));
    }

    getRules() {
        const emailRegex = new RegExp(RegexConstants.Patterns.EmailRfc5322);
        const rules = ValidationRules
            .ensure<CustomerSubscriptionModel, any>(x => x.startDate)
            .required()
            .withMessage("Start date is required")
            .ensure(x => x.emailNotifications)
            .satisfies((x: string[]) => x && x.length > 0 && x.every((email: any) => emailRegex.exec(email.email)))
            .withMessage("Email is not valid")
            .ensure(x => x.ruleCollectionId)
            .required()
            .withMessage("Rule collection is required")
            .on(this.customerSubscriptionModel);

        return rules.rules;
    }

    @computedFrom("customerSubscriptionModel.maxVesselsCount",
        "currentSubscriptionModel.maxVesselsCount",
        "customerSubscriptionModel.subscriptionTypeId",
        "currentSubscriptionModel.subscriptionTypeId")
    get shouldCleanVesselList(): boolean {
        if (this.customerSubscriptionModel) {
            return this.customerSubscriptionModel.maxVesselsCount < this.currentSubscriptionModel.maxVesselsCount //Vessel count decreased
        }
        return false;
    }

    async cleanVesselList() {
        await this.customerSubscriptionService.cleanCustomerVessels(this.customerId);
    }
    
    removeEmail(i: number) {
        if (i == 0)
            this.customerSubscriptionModel.emailNotifications[i].email = "";
        else
            this.customerSubscriptionModel.emailNotifications.splice(i, 1);
    }

    addEmail(email: string = "") {
        if (email) {
            const toRemove = this.customerSubscriptionModel.emailNotifications.filter(x => !x.email);
            for (const r of toRemove)
                this.customerSubscriptionModel.emailNotifications.splice(this.customerSubscriptionModel.emailNotifications.indexOf(r), 1);
        }

        this.customerSubscriptionModel.emailNotifications.push({
            email: email,
            emailRecipientTypeId: 1
        });
    }

    createFullTrialOrder() {
        //Open confirmation button and pass true/false to create opporunity or not
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CreateSalesforceOpporunityConfirmationDialog, {
            onYes: async () => {
                await this.processFullTrialOrder(true);
            },
            onNo: async () => {
                await this.processFullTrialOrder(false);
            }
        }, true);
    }

    async processFullTrialOrder(createOpportunity: boolean) {
        this.isLoading = true;
        let queryParams = this.configService.config.environment.toLowerCase() == "production" ? "" : `env=${this.configService.config.environment}`;      
        if (queryParams) queryParams += "&";
        queryParams += `IncludeCompanyData=${this.customerSubscriptionModel.includeCompanyData}`;
        queryParams += `&CompaniesCount=${this.customerSubscriptionModel.maxCompaniesCount}`;
        queryParams += `&CompanyChangesCount=${this.customerSubscriptionModel.maxCompanyChangesCount}`;
        queryParams += `&StartDate=${moment(this.customerSubscriptionModel.startDate).toISOString()}`;
        queryParams += `&TrialLength=${this.customerSubscriptionModel.trialLength}`;
        queryParams += `&SubscriptionLength=${this.customerSubscriptionModel.subscriptionLength}`;
        //set default subscription type to full for API subscriptions
        queryParams += `&SubscriptionTypeId=${SubscriptionTypeEnum.Full}`;
        queryParams += `&UserId=${this.userClientService?.currentUser?.id}`;
        queryParams += `&createOpportunity=${createOpportunity}`;
        queryParams += `&RuleCollectionId=${this.customerSubscriptionModel.ruleCollectionId}`;
        queryParams += `&${this.convertEmailQuaryString()}`;

        const customerBUIds = await this.customerService.getCustomerBusinessUnitIds(this.customerId);
        if (customerBUIds.businessUnitIds.length == 0) {
            this.isLoading;
            this.notificationClientService.show("There is no business unit assigned to this customer", null, NotificationType.Error);
        } else {
            const purchaseModel: RightStoreCheckoutPurchaseModel = {            
                checkoutQuantity: 1,
                customerNumber: customerBUIds.businessUnitIds[0],
                queryParams: queryParams,
                userName: this.isRightshipUser ? "" : this.userClientService.currentUser.email,
                productNumber: this.productCode
            }
            purchaseModel.paymentMethod = PaymentTypeEnum.Invoice;
            purchaseModel.priceOverride = 0;
    
            try {
                await this.rightStoreService.purchaseProduct(purchaseModel);
                this.notificationClientService.show("Subscription was purchased successfully and will be updated soon!", "", NotificationType.Success);
                this.dialogClientService.hide(true, "default", true);

                if(this.afterSave) {
                    this.afterSave();
                }
            } catch (e) {
                this.isLoading = false;
                this.notificationClientService.show('Error occurs when purchasing product.', '', NotificationType.Error);
            }
        }
    }
}